/**
 * Accounts that also (or only) exist for the CMS, and what they may do in the
 * webshop. Mirrors cms/src/functions/permissions.js (getCmsRole, getScope) —
 * keep both in line. Background: docs/cms-toegang.md.
 *
 * Two kinds of profiles in `users/{uid}` can log in here:
 * - a webshop account (registered in the webshop): it has an `authorization`
 *   block and the review in the CMS decides, as it always has. Such an account
 *   may additionally carry a `cmsRole` for CMS access.
 * - a CMS account (invited through the CMS, CMS role in `role`): it never went
 *   through the webshop review and has no `authorization` block. It gets into
 *   the webshop when it is marked as salesperson (`isSalesPerson`) while it
 *   still has a CMS role, so revoking CMS access also closes the webshop.
 */

/** Fixed set of CMS roles — mirror of cms/src/roles.js. */
export const CMS_ROLES = [
  "cmsAdmin",
  "cmsUser",
  "cmsSales",
  "cmsEmployee",
  "cmsPurchasing",
];

const ROLE_ADMIN = "cmsAdmin";
const ROLE_SALES = "cmsSales";

export const isCmsRole = (role) => CMS_ROLES.includes(role);

/** CMS role of a profile: `cmsRole` first, then `role`; null = no CMS role. */
export const getCmsRole = (profile) => {
  if (!profile) return null;
  if (isCmsRole(profile.cmsRole)) return profile.cmsRole;
  if (isCmsRole(profile.role)) return profile.role;
  return null;
};

/** May this profile use the webshop? */
export const hasWebshopAccess = (profile) => {
  if (!profile) return false;
  if (profile.authorization) return !!profile.authorization.isAuthorized;
  return profile.isSalesPerson === true && !!getCmsRole(profile);
};

/**
 * Has this profile been refused (as opposed to still waiting for review)?
 * Refused profiles are signed out; a webshop registration that is still under
 * review stays signed in, as before.
 */
export const isWebshopAccessRefused = (profile) => {
  if (!profile) return false;
  if (profile.authorization)
    return (
      !!profile.authorization.isReviewed && !profile.authorization.isAuthorized
    );
  return !hasWebshopAccess(profile);
};

export const SCOPE_ALL = "all";
export const SCOPE_COUNTRIES = "countries";
export const SCOPE_CUSTOMERS = "customers";
export const SCOPE_NONE = "none";

const normalizeCountries = (countries) =>
  Array.isArray(countries)
    ? countries
        .map((code) =>
          typeof code === "string" ? code.trim().toUpperCase() : null,
        )
        .filter((code, i, arr) => !!code && arr.indexOf(code) === i)
    : [];

const normalizeIds = (ids) =>
  Array.isArray(ids)
    ? ids.filter(
        (id, i, arr) => typeof id === "string" && !!id && arr.indexOf(id) === i,
      )
    : [];

/**
 * Which customers a CMS account sees — same rules as getScope in the CMS:
 * - `all`: admin; or another role without countries and without customers;
 * - `customers`: `salesCustomerIds` filled (wins over countries);
 * - `countries`: `salesCountries` filled;
 * - `none`: salesperson role without either.
 * Returns null for a profile without a CMS role: a webshop salesperson keeps
 * the webshop's own rule (own customers plus `salesCountries`).
 */
export const getCmsCustomerScope = (profile) => {
  const role = getCmsRole(profile);
  if (!role) return null;
  if (role === ROLE_ADMIN) return { type: SCOPE_ALL };
  const customerIds = normalizeIds(profile.salesCustomerIds);
  if (customerIds.length > 0) return { type: SCOPE_CUSTOMERS, customerIds };
  const countries = normalizeCountries(profile.salesCountries);
  if (countries.length > 0) return { type: SCOPE_COUNTRIES, countries };
  return role === ROLE_SALES ? { type: SCOPE_NONE } : { type: SCOPE_ALL };
};

/**
 * CMS sales reps order on behalf of customers and are not bound to the
 * minimum order amount (any order size, e.g. a top-up of 80 euros). Based on
 * the CMS role, so it also holds for a webshop account with `cmsRole`.
 */
export const isExemptFromMinimumOrder = (profile) =>
  getCmsRole(profile) === ROLE_SALES;

/** Name to put on an order as salesperson; CMS accounts may lack a name. */
export const getDisplayName = (profile) => {
  if (!profile) return null;
  const name = [profile.firstName, profile.lastName]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");
  return name || profile.email || null;
};
